import { useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { RatingStars } from "./RatingStars";
import { z } from "zod";

const ratingSchema = z.object({
  rating: z.number().min(1, "Rating is required").max(5),
  review: z.string().max(500, "Review must be 500 characters or less").optional(),
});

interface RatingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  bookingId: string;
  influencerId: string;
  influencerName: string;
  onRatingSubmitted: () => void;
}

export const RatingDialog = ({
  open,
  onOpenChange,
  bookingId,
  influencerId,
  influencerName,
  onRatingSubmitted,
}: RatingDialogProps) => {
  const { language } = useLanguage();
  const { toast } = useToast();
  const isRTL = language === 'ar';

  const [rating, setRating] = useState(0);
  const [review, setReview] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    // Validate input with Zod
    const validation = ratingSchema.safeParse({
      rating,
      review: review.trim() || undefined,
    });

    if (!validation.success) {
      const firstError = validation.error.errors[0];
      toast({
        title: isRTL ? "خطأ" : "Error",
        description: firstError?.message || (isRTL ? "يرجى اختيار تقييم" : "Please select a rating"),
        variant: "destructive",
      });
      return;
    }

    setSubmitting(true);

    try {
      // Sanitize review text - strip any HTML-like content
      const sanitizedReview = review.trim()
        .replace(/<[^>]*>/g, '') // Remove HTML tags
        .substring(0, 500) || null;

      const { error } = await supabase.from('influencer_ratings').insert({
        booking_id: bookingId,
        influencer_id: influencerId,
        rating: validation.data.rating,
        review: sanitizedReview,
      });

      if (error) {
        if (error.code === '23505') {
          toast({
            title: isRTL ? "تم التقييم مسبقاً" : "Already Rated",
            description: isRTL ? "لقد قمت بتقييم هذا الحجز بالفعل" : "You have already rated this booking",
            variant: "destructive",
          });
        } else {
          throw error;
        }
        return;
      }

      toast({
        title: isRTL ? "شكراً لك!" : "Thank you!",
        description: isRTL ? "تم إرسال تقييمك بنجاح" : "Your rating has been submitted successfully",
      });

      onRatingSubmitted();
      onOpenChange(false);
      setRating(0);
      setReview("");
    } catch (error) {
      console.error('Error submitting rating:', error);
      toast({
        title: isRTL ? "خطأ" : "Error",
        description: isRTL ? "فشل في إرسال التقييم" : "Failed to submit rating",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isRTL ? "تقييم المؤثر" : "Rate Influencer"}
          </DialogTitle>
          <DialogDescription>
            {isRTL
              ? `شاركنا تجربتك مع ${influencerName}`
              : `Share your experience with ${influencerName}`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          <div className="flex flex-col items-center gap-3">
            <Label className="text-center">
              {isRTL ? "ما هو تقييمك؟" : "How would you rate your experience?"}
            </Label>
            <RatingStars
              rating={rating}
              interactive
              onChange={setRating}
              size="lg"
            />
            {rating > 0 && (
              <span className="text-sm text-muted-foreground">
                {rating} {isRTL ? "من 5" : "out of 5"}
              </span>
            )}
          </div>

          <div className="space-y-2">
            <Label>{isRTL ? "مراجعتك (اختياري)" : "Your Review (optional)"}</Label>
            <Textarea
              value={review}
              onChange={(e) => setReview(e.target.value)}
              placeholder={
                isRTL
                  ? "أخبرنا المزيد عن تجربتك..."
                  : "Tell us more about your experience..."
              }
              rows={4}
              maxLength={500}
            />
            <p className="text-xs text-muted-foreground text-right">
              {review.length}/500
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            {isRTL ? "إلغاء" : "Cancel"}
          </Button>
          <Button onClick={handleSubmit} disabled={submitting || rating === 0}>
            {submitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            {isRTL ? "إرسال التقييم" : "Submit Rating"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
